// Plantillas PDF de los carteles, desde el panel. El servidor las guarda en Cloud
// Storage (src/pdfTemplates.ts). Una plantilla cuenta como «cargada» solo cuando el
// servidor lo confirma (§1.5, P-22).
import { AdminAuthError, adminFetch, adminUpload } from "../../api";

export type Orientacion = "horizontal" | "vertical";

/** El mismo tope que el servidor (PDF_TEMPLATE_MAX_BYTES en src/pdfTemplates.ts). */
export const PLANTILLA_MAX_BYTES = 20 * 1024 * 1024;

export interface PlantillaMeta {
  nombre: string;      // nombre original del archivo
  bytes: number;
  actualizada: string; // ISO
}

function aMeta(data: any): PlantillaMeta | null {
  if (!data || !data.exists) return null;
  return { nombre: String(data.name), bytes: Number(data.size), actualizada: String(data.updated) };
}

async function errorDeRespuesta(res: Response): Promise<Error> {
  try {
    const data = await res.json();
    if (data?.error) return new Error(data.error);
  } catch {
    // sin JSON: queda el mensaje genérico
  }
  return new Error(`El servidor respondió ${res.status}.`);
}

/** null si no hay plantilla guardada. */
export async function leerMetaPlantilla(orientacion: Orientacion): Promise<PlantillaMeta | null> {
  const res = await adminFetch(`/api/pdf-template/${orientacion}/meta`, { cache: "no-store" });
  if (!res.ok) throw await errorDeRespuesta(res);
  return aMeta(await res.json());
}

export async function bajarPlantilla(orientacion: Orientacion): Promise<ArrayBuffer> {
  const res = await adminFetch(`/api/pdf-template/${orientacion}`, { cache: "no-store" });
  if (!res.ok) throw await errorDeRespuesta(res);
  return res.arrayBuffer();
}

/** Devuelve lo que quedó guardado; si el servidor no lo confirma, tira error. */
export async function subirPlantilla(
  orientacion: Orientacion,
  archivo: File,
  onAvance?: (porcentaje: number) => void
): Promise<PlantillaMeta> {
  const esPdf = archivo.type === "application/pdf" || archivo.name.toLowerCase().endsWith(".pdf");
  if (!esPdf) throw new Error("El archivo no es un PDF.");
  if (archivo.size > PLANTILLA_MAX_BYTES) throw new Error("El PDF pesa más de 20 MB.");
  const { status, data } = await adminUpload(
    `/api/pdf-template/${orientacion}`,
    archivo,
    { "Content-Type": "application/pdf", "X-File-Name": encodeURIComponent(archivo.name) },
    onAvance
  );
  if (status < 200 || status >= 300) throw new Error(data?.error || `El servidor respondió ${status}.`);
  const meta = aMeta(data);
  if (!meta) throw new Error("El servidor no confirmó la plantilla.");
  return meta;
}

/** El motivo, en palabras del panel, de por qué falló algo con una plantilla. */
export function motivoDeFalla(e: unknown, accion?: "subir"): string {
  if (e instanceof AdminAuthError) {
    if (e.message.startsWith("Tu cuenta no tiene permiso")) return e.message;
    return accion === "subir"
      ? "Tu sesión venció: volvé a entrar y subila de nuevo."
      : "Tu sesión venció: volvé a entrar.";
  }
  if (e instanceof TypeError) return "No hubo conexión con el servidor.";
  return e instanceof Error && e.message ? e.message : "Algo falló; probá de nuevo.";
}
